import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const DAYS = [["Sun", 0], ["Mon", 1], ["Tue", 2], ["Wed", 3], ["Thu", 4], ["Fri", 5], ["Sat", 6]];
const blank = { title: "", message: "", days: [], start_time: "18:00", end_time: "21:00", trigger: "schedule", hours_before: 3, enabled: true, order: 0 };

function fmtDays(days) {
  if (!days || days.length === 0) return "No days set";
  if (days.length === 7) return "Every day";
  return DAYS.filter(([, d]) => days.includes(d)).map(([l]) => l).join(", ");
}
function fmt12(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

const SEL = "h-11 rounded-xl border border-black/10 bg-white px-3 text-sm text-brand-forest focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40";

function TimeSelect({ value, onChange, testid }) {
  const [hStr, mStr] = (value || "00:00").split(":");
  const h24 = Number(hStr) || 0;
  const m = Number(mStr) || 0;
  const ap = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  const mins = Array.from(new Set([...Array.from({ length: 12 }, (_, i) => i * 5), m])).sort((a, b) => a - b);
  const commit = (nh12, nm, nap) => {
    let H = nh12 % 12;
    if (nap === "PM") H += 12;
    onChange(`${String(H).padStart(2, "0")}:${String(nm).padStart(2, "0")}`);
  };
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <select value={h12} onChange={(e) => commit(Number(e.target.value), m, ap)} data-testid={`${testid}-hour`} className={SEL}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-brand-forest/50 font-semibold">:</span>
      <select value={m} onChange={(e) => commit(h12, Number(e.target.value), ap)} data-testid={`${testid}-min`} className={SEL}>
        {mins.map((mm) => <option key={mm} value={mm}>{String(mm).padStart(2, "0")}</option>)}
      </select>
      <select value={ap} onChange={(e) => commit(h12, m, e.target.value)} data-testid={`${testid}-ap`} className={SEL}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export default function EditReminders() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/reminders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...blank, order: items.length });
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    if (editing.trigger !== "checkout" && editing.days.length === 0) { toast.error("Pick at least one day"); return; }
    if (editing.id) await api.put(`/reminders/${editing.id}`, editing);
    else await api.post("/reminders", editing);
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => { await api.delete(`/reminders/${id}`); toast.success("Deleted"); load(); };
  const toggleDay = (d) => {
    const has = editing.days.includes(d);
    setEditing({ ...editing, days: has ? editing.days.filter((x) => x !== d) : [...editing.days, d] });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="overline text-brand-terracotta">Edit</span>
          <h1 className="font-display text-4xl tracking-tight mt-1">Reminders</h1>
          <p className="text-sm text-brand-forest/60 mt-1">Full-screen alerts that pop up on the tablet on the days/times you set, until a guest taps to dismiss.</p>
        </div>
        <Button onClick={openNew} data-testid="add-reminder" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Plus size={16} className="mr-2" />Add</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-reminder-${it.id}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm flex justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg tracking-tight">{it.title}</h3>
                <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${it.enabled ? "bg-brand-sand text-brand-terracotta" : "bg-brand-bg text-brand-forest/40"}`}>{it.enabled ? "On" : "Off"}</span>
              </div>
              <p className="text-sm text-brand-forest/60 line-clamp-2 mt-1">{it.message}</p>
              <p className="text-xs text-brand-forest/50 mt-2">{it.trigger === "checkout" ? `${it.hours_before ?? 3}h before check-out` : `${fmtDays(it.days)} · ${fmt12(it.start_time)}–${fmt12(it.end_time)}`}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => setEditing(it)} data-testid={`edit-reminder-${it.id}`} className="text-sm font-medium text-brand-terracotta hover:underline">Edit</button>
              <button onClick={() => remove(it.id)} data-testid={`delete-reminder-${it.id}`} className="text-brand-forest/30 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="reminder-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Reminder</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch data-testid="reminder-enabled" checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />
              </div>
              <div><Label className="mb-1.5 block">Title</Label><Input data-testid="reminder-title-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Trash & Recycle Night" /></div>
              <div>
                <Label className="mb-1.5 block">Trigger</Label>
                <div className="flex rounded-full bg-brand-bg border border-black/5 p-1" data-testid="reminder-trigger">
                  <button type="button" data-testid="reminder-trigger-schedule" onClick={() => setEditing({ ...editing, trigger: "schedule" })}
                    className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${(editing.trigger || "schedule") === "schedule" ? "bg-brand-forest text-white" : "text-brand-forest/60"}`}>On a schedule</button>
                  <button type="button" data-testid="reminder-trigger-checkout" onClick={() => setEditing({ ...editing, trigger: "checkout" })}
                    className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${editing.trigger === "checkout" ? "bg-brand-forest text-white" : "text-brand-forest/60"}`}>Before check-out</button>
                </div>
              </div>
              {editing.trigger === "checkout" ? (
                <>
                  <div>
                    <Label className="mb-1.5 block">Hours prior to check-out</Label>
                    <Input data-testid="reminder-hours-before" type="number" min={1} max={72}
                      value={editing.hours_before ?? 3}
                      onChange={(e) => setEditing({ ...editing, hours_before: Math.max(1, Math.min(72, Number(e.target.value) || 1)) })} />
                  </div>
                  <div><Label className="mb-1.5 block">Move-out instructions</Label><Textarea data-testid="reminder-checkout-message" rows={5} value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} placeholder="Please load and start the dishwasher, place used towels in the tub, take out any trash, and lock the front door on your way out…" /></div>
                  <p className="text-xs text-brand-forest/50">On the guest's check-out day, this full-screen alert appears <strong>{editing.hours_before ?? 3} hour(s) before check-out</strong>, showing the <strong>heading</strong> (Title) and the <strong>move-out instructions</strong> above. Leave the instructions blank to fall back to your Check-out Instructions from Property Info. The guest taps to dismiss.</p>
                </>
              ) : (
                <>
                  <div><Label className="mb-1.5 block">Message</Label><Textarea data-testid="reminder-message" rows={3} value={editing.message} onChange={(e) => setEditing({ ...editing, message: e.target.value })} placeholder="Please take the bins to the curb tonight…" /></div>
                  <div>
                    <Label className="mb-1.5 block">Repeat on</Label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS.map(([label, d]) => (
                        <button
                          key={d} type="button" onClick={() => toggleDay(d)} data-testid={`reminder-day-${d}`}
                          className={`h-11 w-11 rounded-full text-sm font-semibold transition-colors active:scale-95 ${editing.days.includes(d) ? "bg-brand-forest text-white" : "bg-brand-bg text-brand-forest/60 border border-black/5 hover:text-brand-terracotta"}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label className="mb-1.5 block">Show from</Label><TimeSelect testid="reminder-start" value={editing.start_time} onChange={(v) => setEditing({ ...editing, start_time: v })} /></div>
                    <div><Label className="mb-1.5 block">Show until</Label><TimeSelect testid="reminder-end" value={editing.end_time} onChange={(v) => setEditing({ ...editing, end_time: v })} /></div>
                  </div>
                  <p className="text-xs text-brand-forest/50">The alert appears during this window and stays full-screen until a guest taps to dismiss it (once dismissed, it won't return until the next scheduled window).</p>
                </>
              )}
            </div>
          )}
          <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white/95 backdrop-blur border-t border-black/5">
            <Button onClick={save} data-testid="reminder-save" className="w-full sm:w-auto rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Save size={16} className="mr-2" />Save Reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
